import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import LockedFeature from '../components/LockedFeature';
import feesService from '../services/feesService';
import schoolsService from '../services/schoolsService';
import classesService from '../services/classesService';
import campusService from '../services/campusService';
import settlementsService from '../services/settlementsService';
import FeeReminderSMSModal from '../components/communication/FeeReminderSMSModal';
import MoMoWithdrawalModal from '../components/MoMoWithdrawalModal';
import FeeDetailsModal from '../components/FeeDetailsModal';
import './FeesPage.css';
import {
  Plus, X, CurrencyCircleDollar, Receipt, Users, ChartBar,
  MagnifyingGlass, CheckCircle, Warning, Clock, CaretDown,
  Printer, Download, Phone, ArrowDown, Wallet, CircleNotch, Info,
  Circle
} from '@phosphor-icons/react';

// Payment methods
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'mobile_money', label: 'Mobile Money (MoMo)' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
];

// Fee types
const FEE_TYPES = [
  { value: 'tuition', label: 'Tuition Fee' },
  { value: 'examination', label: 'Examination Fee' },
  { value: 'sports', label: 'Sports Fee' },
  { value: 'ict', label: 'ICT Fee' },
  { value: 'library', label: 'Library Fee' },
  { value: 'maintenance', label: 'Maintenance Fee' },
  { value: 'pta', label: 'PTA Levy' },
  { value: 'other', label: 'Other' },
];

// Class levels for Ghana
const CLASS_LEVELS = [
  { value: 'kg1', label: 'KG 1' },
  { value: 'kg2', label: 'KG 2' },
  { value: 'primary_1', label: 'Primary 1' },
  { value: 'primary_2', label: 'Primary 2' },
  { value: 'primary_3', label: 'Primary 3' },
  { value: 'primary_4', label: 'Primary 4' },
  { value: 'primary_5', label: 'Primary 5' },
  { value: 'primary_6', label: 'Primary 6' },
  { value: 'jhs_1', label: 'JHS 1' },
  { value: 'jhs_2', label: 'JHS 2' },
  { value: 'jhs_3', label: 'JHS 3' },
];

function formatCurrency(amount) {
  // Ensure amount is a valid number
  const numAmount = parseFloat(amount) || 0;
  return `GHS ${numAmount.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function FeesPage() {
  const { user } = useAuth();
  const [view, setView] = useState('overview'); // overview, structures, class, settlements
  const [summary, setSummary] = useState(null);
  const [structures, setStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicTerms, setAcademicTerms] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStructureClassFilter, setSelectedStructureClassFilter] = useState('');
  const [classStatus, setClassStatus] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Settlement states
  const [balance, setBalance] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [showMoMoWithdrawalModal, setShowMoMoWithdrawalModal] = useState(false);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [schoolPhoneNumber, setSchoolPhoneNumber] = useState('');
  const [schoolName, setSchoolName] = useState('');
  
  // Modals
  const [showStructureModal, setShowStructureModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showFeeReminderSMSModal, setShowFeeReminderSMSModal] = useState(false);
  const [showFeeDetailsModal, setShowFeeDetailsModal] = useState(false);
  const [feeDetailsStudent, setFeeDetailsStudent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedFee, setSelectedFee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Dialog Modal
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });
  
  // Forms
  const [structureForm, setStructureForm] = useState({
    academic_term_id: '',
    class_level: 'primary_1',
    campus_id: '',
    fee_type: 'tuition',
    amount: '',
    description: '',
    is_mandatory: true,
    due_date: new Date().toISOString().split('T')[0]
  });
  const [campuses, setCampuses] = useState([]);
  
  const [paymentForm, setPaymentForm] = useState({
    fee_id: '',
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    remarks: ''
  });
  
  const [assignForm, setAssignForm] = useState({
    structure_id: '',
    class_id: ''
  });

  // Dialog helpers
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  // Load fee summary
  useEffect(() => {
    feesService.getSummary()
      .then(data => {
        // Ensure all required fields exist with defaults
        const sanitizedData = {
          total_expected: data?.total_expected || 0,
          total_collected: data?.total_collected || 0,
          total_outstanding: data?.total_outstanding || 0,
          total_discount: data?.total_discount || 0,
          collection_rate: data?.collection_rate || 0,
          total_students_with_fees: data?.total_students_with_fees || 0,
          status_breakdown: data?.status_breakdown || { paid: 0, partial: 0, pending: 0, overdue: 0 }
        };
        setSummary(sanitizedData);
      })
      .catch(err => {
        // Set default summary on error
        setSummary({
          total_expected: 0,
          total_collected: 0,
          total_outstanding: 0,
          total_discount: 0,
          collection_rate: 0,
          total_students_with_fees: 0,
          status_breakdown: { paid: 0, partial: 0, pending: 0, overdue: 0 }
        });
      });
  }, []);

  // Load fee structures
  useEffect(() => {
    feesService.listStructures()
      .then(data => setStructures(data || []))
      .catch(err => {});
  }, []);

  // Load classes
  useEffect(() => {
    classesService.listClasses()
      .then(data => setClasses(data || []))
      .catch(err => {});
  }, []);

  // Load campuses (for scoping fee structures — empty for schools with no campuses set up)
  useEffect(() => {
    campusService.listCampuses()
      .then(data => setCampuses(Array.isArray(data) ? data : []))
      .catch(() => setCampuses([]));
  }, []);

  // Load academic terms (for the fee structure form's term selector)
  useEffect(() => {
    if (!user?.school_id) return;
    schoolsService.listTerms(user.school_id)
      .then(data => {
        const terms = data || [];
        setAcademicTerms(terms);
        if (terms.length > 0) {
          const currentTerm = terms.find(t => t.is_current);
          setStructureForm(prev => ({
            ...prev,
            academic_term_id: prev.academic_term_id || (currentTerm ? currentTerm.id : terms[0].id)
          }));
        }
      })
      .catch(() => {});
  }, [user]);

  // Load school phone number and name for withdrawal modal
  useEffect(() => {
    schoolsService.listSchools()
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const school = data[0];
          if (school?.phone) {
            setSchoolPhoneNumber(school.phone);
          }
          if (school?.name) {
            setSchoolName(school.name);
          }
        }
      })
      .catch(err => {});
  }, []);

  // Load outstanding fees
  useEffect(() => {
    if (view === 'overview') {
      feesService.getOutstanding()
        .then(data => setOutstanding(data))
        .catch(err => {});
    }
  }, [view]);

  // Load class fee status
  const loadClassStatus = useCallback(() => {
    if (!selectedClass) return;
    setLoading(true);
    feesService.getClassFees(selectedClass)
      .then(data => setClassStatus(data))
      .catch(err => toast.error('Failed to load class fees'))
      .finally(() => setLoading(false));
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass && view === 'class') {
      loadClassStatus();
    }
  }, [selectedClass, view, loadClassStatus]);

  // Load settlement data
  const loadSettlementData = useCallback(async () => {
    setSettlementLoading(true);
    try {
      const [balanceData, transactionsData, historyData] = await Promise.all([
        settlementsService.getBalance(),
        settlementsService.getRecentTransactions(7),
        settlementsService.getWithdrawalHistory()
      ]);
      
      setBalance(balanceData.balance);
      setRecentTransactions(transactionsData.transactions || []);
      setWithdrawalHistory(historyData.withdrawals || []);
    } catch (err) {
      toast.error('Failed to load settlement data');
    } finally {
      setSettlementLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'settlements') {
      loadSettlementData();
    }
  }, [view, loadSettlementData]);

  // Create fee structure
  const handleCreateStructure = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await feesService.createStructure({
        ...structureForm,
        campus_id: structureForm.campus_id || null,
        amount: parseFloat(structureForm.amount)
      });
      toast.success('Fee structure created!');
      setShowStructureModal(false);
      setStructureForm({
        academic_term_id: structureForm.academic_term_id,
        class_level: 'primary_1',
        campus_id: '',
        fee_type: 'tuition',
        amount: '',
        description: '',
        is_mandatory: true,
        due_date: new Date().toISOString().split('T')[0]
      });
      const data = await feesService.listStructures();
      setStructures(data || []);
    } catch (error) {
      showDialog({
        title: 'Failed to Create Fee Structure',
        message: error.message || 'An error occurred while creating the fee structure. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Record payment
  const handleRecordPayment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await feesService.recordPayment({
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      toast.success(`Payment recorded! Receipt: ${result.receipt_number}`);
      setShowPaymentModal(false);
      setPaymentForm({
        fee_id: '',
        amount: '',
        payment_method: 'cash',
        reference_number: '',
        payment_date: new Date().toISOString().split('T')[0],
        remarks: ''
      });
      loadClassStatus();
      // Refresh summary
      const summaryData = await feesService.getSummary();
      setSummary(summaryData);
    } catch (error) {
      showDialog({
        title: 'Payment Recording Failed',
        message: error.message || 'An error occurred while recording the payment. Please verify the details and try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Assign fee to class
  const handleAssignFee = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await feesService.assignToClass(assignForm.structure_id, assignForm.class_id);
      toast.success(result.message);
      setShowAssignModal(false);
      loadClassStatus();
    } catch (error) {
      showDialog({
        title: 'Failed to Assign Fee',
        message: error.message || 'An error occurred while assigning the fee to the class. Please try again.',
        type: 'error',
        primaryAction: {
          label: 'OK',
          onClick: closeDialog
        }
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Open payment modal for a student
  const openPaymentForStudent = async (student) => {
    setSelectedStudent(student);
    try {
      const data = await feesService.getStudentFees(student.student_id);
      if (data.fees && data.fees.length > 0) {
        // Get first unpaid fee
        const unpaidFee = data.fees.find(f => f.status !== 'paid');
        if (unpaidFee) {
          setSelectedFee(unpaidFee);
          setPaymentForm({
            ...paymentForm,
            fee_id: unpaidFee.id,
            amount: unpaidFee.balance.toString()
          });
        }
      }
      setShowPaymentModal(true);
    } catch (error) {
      toast.error('Failed to load student fees');
    }
  };

  const openFeeDetails = (student) => {
    setFeeDetailsStudent({ id: student.student_id, name: student.student_name });
    setShowFeeDetailsModal(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: { bg: 'rgba(5, 150, 105, 0.1)', color: '#059669', icon: CheckCircle },
      partial: { bg: 'rgba(217, 119, 6, 0.1)', color: '#D97706', icon: Clock },
      pending: { bg: 'rgba(220, 38, 38, 0.1)', color: '#DC2626', icon: Warning },
      no_fees: { bg: 'rgba(161, 161, 170, 0.1)', color: '#A1A1AA', icon: null },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <span className="status-badge" style={{ backgroundColor: style.bg, color: style.color }}>
        {Icon && <Icon size={14} weight="bold" />}
        {status.replace('_', ' ')}
      </span>
    );
  };

  return (
    <Layout>
      <div className="page" data-testid="fees-page">
        <div className="page-header">
          <div>
            <h1>Fee Management</h1>
            <p>Track school fees, payments, and collections</p>
          </div>
          <div className="header-actions">
            <LockedFeature module="fees_plus">
              <button
                className="btn btn-secondary"
                onClick={() => setShowStructureModal(true)}
                data-testid="add-structure-btn"
              >
                <Plus size={18} /> Fee Structure
              </button>
            </LockedFeature>
            <LockedFeature module="fees_plus">
              <button
                className="btn btn-primary"
                onClick={() => setShowAssignModal(true)}
                data-testid="assign-fee-btn"
              >
                <Users size={18} /> Assign to Class
              </button>
            </LockedFeature>
          </div>
        </div>

        {/* View Tabs */}
        <div className="tabs-container">
          <button 
            className={`tab-btn ${view === 'overview' ? 'active' : ''}`}
            onClick={() => setView('overview')}
          >
            <ChartBar size={18} /> Overview
          </button>
          <LockedFeature module="fees_plus">
            <button
              className={`tab-btn ${view === 'structures' ? 'active' : ''}`}
              onClick={() => setView('structures')}
            >
              <CurrencyCircleDollar size={18} /> Fee Structures
            </button>
          </LockedFeature>
          <LockedFeature module="fees_plus">
            <button
              className={`tab-btn ${view === 'class' ? 'active' : ''}`}
              onClick={() => setView('class')}
            >
              <Users size={18} /> By Class
            </button>
          </LockedFeature>
          <LockedFeature module="fees_plus">
            <button
              className={`tab-btn ${view === 'settlements' ? 'active' : ''}`}
              onClick={() => setView('settlements')}
            >
              <ArrowDown size={18} /> Settlements
            </button>
          </LockedFeature>
        </div>

        {/* Overview View */}
        {view === 'overview' && (
          <div className="fees-overview">
            {/* Summary Cards */}
            {summary && (
              <div className="fee-summary-grid">
                <div className="fee-summary-card total">
                  <div className="summary-icon">
                    <CurrencyCircleDollar size={28} weight="duotone" />
                  </div>
                  <div className="summary-content">
                    <span className="summary-label">Total Expected</span>
                    <span className="summary-value">{formatCurrency(summary?.total_expected ?? 0)}</span>
                  </div>
                </div>
                <div className="fee-summary-card collected">
                  <div className="summary-icon">
                    <CheckCircle size={28} weight="duotone" />
                  </div>
                  <div className="summary-content">
                    <span className="summary-label">Total Collected</span>
                    <span className="summary-value">{formatCurrency(summary?.total_collected ?? 0)}</span>
                  </div>
                </div>
                <div className="fee-summary-card outstanding">
                  <div className="summary-icon">
                    <Warning size={28} weight="duotone" />
                  </div>
                  <div className="summary-content">
                    <span className="summary-label">Outstanding</span>
                    <span className="summary-value">{formatCurrency(summary?.total_outstanding ?? 0)}</span>
                  </div>
                </div>
                <div className="fee-summary-card rate">
                  <div className="summary-icon">
                    <ChartBar size={28} weight="duotone" />
                  </div>
                  <div className="summary-content">
                    <span className="summary-label">Collection Rate</span>
                    <span className="summary-value">{summary?.collection_rate ?? 0}%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Status Breakdown */}
            {summary?.status_breakdown && (
              <div className="status-breakdown">
                <h3>Payment Status Breakdown</h3>
                <div className="status-bars">
                  <div className="status-bar-item">
                    <span className="status-label">Fully Paid</span>
                    <div className="status-bar">
                      <div className="status-fill paid" style={{ width: `${(summary.status_breakdown.paid / summary.total_students_with_fees * 100) || 0}%` }}></div>
                    </div>
                    <span className="status-count">{summary.status_breakdown.paid}</span>
                  </div>
                  <div className="status-bar-item">
                    <span className="status-label">Partial</span>
                    <div className="status-bar">
                      <div className="status-fill partial" style={{ width: `${(summary.status_breakdown.partial / summary.total_students_with_fees * 100) || 0}%` }}></div>
                    </div>
                    <span className="status-count">{summary.status_breakdown.partial}</span>
                  </div>
                  <div className="status-bar-item">
                    <span className="status-label">Pending</span>
                    <div className="status-bar">
                      <div className="status-fill pending" style={{ width: `${(summary.status_breakdown.pending / summary.total_students_with_fees * 100) || 0}%` }}></div>
                    </div>
                    <span className="status-count">{summary.status_breakdown.pending}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Outstanding Fees List */}
            {outstanding && outstanding.students.length > 0 && (
              <div className="outstanding-section">
                <div className="section-header">
                  <h3>Students with Outstanding Fees</h3>
                  <span className="badge warning">{outstanding.total_students} students</span>
                </div>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>ID</th>
                        <th>Total Due</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstanding.students.slice(0, 10).map(student => (
                        <tr key={student.student_id}>
                          <td>{student.student_name}</td>
                          <td className="mono">{student.student_number}</td>
                          <td>{formatCurrency(student.total_due)}</td>
                          <td className="text-success">{formatCurrency(student.total_paid)}</td>
                          <td className="text-danger font-bold">{formatCurrency(student.balance)}</td>
                          <td>
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => openPaymentForStudent(student)}
                            >
                              Record Payment
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setSelectedStudent({
                                  id: student.student_id,
                                  first_name: student.student_name.split(' ')[0],
                                  last_name: student.student_name.split(' ').slice(1).join(' '),
                                  parent_name: student.parent_name,
                                  parent_phone: student.parent_phone,
                                  balance: student.balance
                                });
                                setShowFeeReminderSMSModal(true);
                              }}
                              title="Send SMS reminder"
                            >
                              <Phone size={14} /> SMS
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => openFeeDetails(student)}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fee Structures View */}
        {view === 'structures' && (
          <div className="structures-content">
            <div className="filters-row">
              <div className="form-group">
                <label>Filter by Class</label>
                <select
                  value={selectedStructureClassFilter}
                  onChange={(e) => setSelectedStructureClassFilter(e.target.value)}
                  data-testid="structure-class-filter"
                >
                  <option value="">All Classes</option>
                  {CLASS_LEVELS.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
            </div>
            {structures.length === 0 ? (
              <div className="empty-state-card">
                <CurrencyCircleDollar size={48} weight="duotone" />
                <h3>No Fee Structures</h3>
                <p>Create fee structures to start collecting fees</p>
                <button className="btn btn-primary" onClick={() => setShowStructureModal(true)}>
                  Create Fee Structure
                </button>
              </div>
            ) : (
              <div className="structures-grid">
                {structures
                  .filter(s => !selectedStructureClassFilter || s.class_level === selectedStructureClassFilter)
                  .map(structure => (
                  <div key={structure.id} className="structure-card" data-testid={`structure-${structure.id}`}>
                    <div className="structure-header">
                      <span className={`fee-type-badge ${structure.fee_type}`}>
                        {FEE_TYPES.find(t => t.value === structure.fee_type)?.label || structure.fee_type}
                      </span>
                      {structure.is_mandatory && <span className="mandatory-badge">Mandatory</span>}
                    </div>
                    <div className="structure-amount">{formatCurrency(structure.amount)}</div>
                    <div className="structure-details">
                      <div className="detail-row">
                        <span>Class Level:</span>
                        <span className="capitalize">{structure.class_level.replace('_', ' ')}</span>
                      </div>
                      {campuses.length > 0 && (
                        <div className="detail-row">
                          <span>Campus:</span>
                          <span>{structure.campus_id ? (campuses.find(c => c.id === structure.campus_id)?.name || 'Unknown') : 'All campuses'}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span>Due Date:</span>
                        <span>{structure.due_date}</span>
                      </div>
                      {structure.description && (
                        <div className="structure-desc">{structure.description}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Class View */}
        {view === 'class' && (
          <div className="class-fees-content">
            <div className="filters-row">
              <div className="form-group">
                <label>Select Class</label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  data-testid="class-select"
                >
                  <option value="">Choose a class...</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {loading ? (
              <div className="loading">Loading class fees...</div>
            ) : classStatus ? (
              <div className="class-fees-view">
                {/* Class Summary */}
                <div className="class-fee-summary">
                  <h3>{classStatus.class_name}</h3>
                  <div className="class-summary-stats">
                    <div className="summary-stat">
                      <span className="stat-label">Total Due</span>
                      <span className="stat-value">{formatCurrency(classStatus.class_summary.total_due)}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Collected</span>
                      <span className="stat-value text-success">{formatCurrency(classStatus.class_summary.total_paid)}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Outstanding</span>
                      <span className="stat-value text-danger">{formatCurrency(classStatus.class_summary.balance)}</span>
                    </div>
                    <div className="summary-stat">
                      <span className="stat-label">Collection Rate</span>
                      <span className="stat-value">{classStatus.class_summary.collection_rate}%</span>
                    </div>
                  </div>
                </div>

                {/* Students Table */}
                <div className="table-container">
                  <table className="data-table" data-testid="class-fees-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>ID</th>
                        <th>Total Due</th>
                        <th>Paid</th>
                        <th>Balance</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {classStatus.students.map(student => (
                        <tr key={student.student_id} data-testid={`fee-row-${student.student_id}`}>
                          <td>{student.student_name}</td>
                          <td className="mono">{student.student_number}</td>
                          <td>{formatCurrency(student.total_due)}</td>
                          <td className="text-success">{formatCurrency(student.total_paid)}</td>
                          <td className={student.balance > 0 ? 'text-danger font-bold' : ''}>
                            {formatCurrency(student.balance)}
                          </td>
                          <td>{getStatusBadge(student.status)}</td>
                          <td>
                            {student.balance > 0 && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => openPaymentForStudent(student)}
                                data-testid={`pay-btn-${student.student_id}`}
                              >
                                Pay
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => openFeeDetails(student)}
                              data-testid={`details-btn-${student.student_id}`}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                      {classStatus.students.length === 0 && (
                        <tr>
                          <td colSpan="7" className="empty-state">No students in this class</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="empty-state-card">
                <Users size={48} weight="duotone" />
                <h3>Select a Class</h3>
                <p>Choose a class to view fee collection status</p>
              </div>
            )}
          </div>
        )}

        {/* Create Fee Structure Modal */}
        {showStructureModal && (
          <div className="modal-overlay" onClick={() => setShowStructureModal(false)}>
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="structure-modal">
              <div className="modal-header">
                <h2>Create Fee Structure</h2>
                <button className="modal-close" onClick={() => setShowStructureModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleCreateStructure} className="modal-form">
                {academicTerms.length === 0 ? (
                  <div className="form-group">
                    <p className="text-danger">
                      No academic terms exist for this school yet. Create one before adding fee structures.
                    </p>
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Academic Term</label>
                    <select
                      value={structureForm.academic_term_id}
                      onChange={(e) => setStructureForm({...structureForm, academic_term_id: e.target.value})}
                      required
                      disabled={submitting}
                      data-testid="structure-term-select"
                    >
                      <option value="">Select a term...</option>
                      {academicTerms.map(term => (
                        <option key={term.id} value={term.id}>
                          {term.academic_year} - Term {term.term}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Fee Type</label>
                    <select
                      value={structureForm.fee_type}
                      onChange={(e) => setStructureForm({...structureForm, fee_type: e.target.value})}
                      disabled={submitting}
                    >
                      {FEE_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Class Level</label>
                    <select
                      value={structureForm.class_level}
                      onChange={(e) => setStructureForm({...structureForm, class_level: e.target.value})}
                      disabled={submitting}
                    >
                      {CLASS_LEVELS.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {campuses.length > 0 && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Campus</label>
                      <select
                        value={structureForm.campus_id}
                        onChange={(e) => setStructureForm({...structureForm, campus_id: e.target.value})}
                        disabled={submitting}
                      >
                        <option value="">All campuses (school-wide)</option>
                        {campuses.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount (GHS)</label>
                    <input
                      type="number"
                      value={structureForm.amount}
                      onChange={(e) => setStructureForm({...structureForm, amount: e.target.value})}
                      placeholder="e.g., 500.00"
                      step="0.01"
                      min="0"
                      required
                      disabled={submitting}
                      data-testid="amount-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={structureForm.due_date}
                      onChange={(e) => setStructureForm({...structureForm, due_date: e.target.value})}
                      required
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    value={structureForm.description}
                    onChange={(e) => setStructureForm({...structureForm, description: e.target.value})}
                    placeholder="Additional details about this fee..."
                    rows={2}
                    disabled={submitting}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={structureForm.is_mandatory}
                      onChange={(e) => setStructureForm({...structureForm, is_mandatory: e.target.checked})}
                      disabled={submitting}
                    />
                    <span>Mandatory fee (required for all students)</span>
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowStructureModal(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting || !structureForm.academic_term_id}
                    data-testid="save-structure-btn"
                  >
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : 'Create Structure'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Record Payment Modal */}
        {showPaymentModal && (
          <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="payment-modal">
              <div className="modal-header">
                <h2>Record Payment</h2>
                <button className="modal-close" onClick={() => setShowPaymentModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleRecordPayment} className="modal-form">
                {selectedStudent && (
                  <div className="payment-student-info">
                    <strong>{selectedStudent.student_name}</strong>
                    <span className="mono">{selectedStudent.student_number}</span>
                    {selectedFee && (
                      <div className="fee-balance">
                        Balance: <strong className="text-danger">{formatCurrency(selectedFee.balance)}</strong>
                      </div>
                    )}
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Amount (GHS)</label>
                    <input
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                      step="0.01"
                      min="0.01"
                      required
                      disabled={submitting}
                      data-testid="payment-amount"
                    />
                  </div>
                  <div className="form-group">
                    <label>Payment Method</label>
                    <select
                      value={paymentForm.payment_method}
                      onChange={(e) => setPaymentForm({...paymentForm, payment_method: e.target.value})}
                      disabled={submitting}
                    >
                      {PAYMENT_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Payment Date</label>
                    <input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                      required
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label>Reference Number (Optional)</label>
                    <input
                      type="text"
                      value={paymentForm.reference_number}
                      onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                      placeholder="e.g., MoMo transaction ID"
                      disabled={submitting}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Remarks (Optional)</label>
                  <textarea
                    value={paymentForm.remarks}
                    onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                    placeholder="Additional notes..."
                    rows={2}
                    disabled={submitting}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPaymentModal(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="confirm-payment-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : (
                      <>
                        <Receipt size={18} /> Record Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assign Fee to Class Modal */}
        {showAssignModal && (
          <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} data-testid="assign-modal">
              <div className="modal-header">
                <h2>Assign Fee to Class</h2>
                <button className="modal-close" onClick={() => setShowAssignModal(false)}>
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleAssignFee} className="modal-form">
                <div className="form-group">
                  <label>Fee Structure</label>
                  <select
                    value={assignForm.structure_id}
                    onChange={(e) => setAssignForm({...assignForm, structure_id: e.target.value})}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select fee structure...</option>
                    {structures.map(s => (
                      <option key={s.id} value={s.id}>
                        {FEE_TYPES.find(t => t.value === s.fee_type)?.label} - {formatCurrency(s.amount)} ({s.class_level.replace('_', ' ')})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Class</label>
                  <select
                    value={assignForm.class_id}
                    onChange={(e) => setAssignForm({...assignForm, class_id: e.target.value})}
                    required
                    disabled={submitting}
                  >
                    <option value="">Select class...</option>
                    {classes.map(cls => (
                      <option key={cls.id} value={cls.id}>{cls.name}</option>
                    ))}
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)} disabled={submitting}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="confirm-assign-btn">
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : 'Assign Fee'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Settlements View */}
        {view === 'settlements' && (
          <div className="settlements-content">
            {settlementLoading ? (
              <div className="loading-state-full">
                <div className="spinner"></div>
                <p>Loading settlement data...</p>
              </div>
            ) : (
              <>
                {/* Header Section */}
                <div className="settlements-header-section">
                  <div className="header-content">
                    <h1>Settlement Management</h1>
                    <p className="subtitle">Monitor your school wallet and manage fund withdrawals</p>
                  </div>
                </div>

                {/* Support Banner */}
                <div className="alert-banner alert-info" style={{
                  backgroundColor: '#DBEAFE',
                  borderLeft: '4px solid #0EA5E9',
                  padding: '14px 16px',
                  borderRadius: '8px',
                  marginBottom: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <Info size={22} weight="fill" style={{ color: '#0284C7', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, color: '#0C4A6E', fontSize: '14px', lineHeight: '1.5' }}>
                      If your withdrawal is not received within 30 minutes, please contact support at <strong>+233 53 448 4781</strong>
                    </p>
                  </div>
                </div>

                {/* Main Balance Card - Prominent */}
                <div className="balance-section">
                  <div className="balance-card-main">
                    <div className="balance-card-wrapper">
                      <div className="balance-card-left">
                        <div className="balance-icon-container">
                          <Wallet size={40} weight="duotone" />
                        </div>
                        <div className="balance-text-group">
                          <span className="balance-label">Current Wallet Balance</span>
                          <div className="balance-value-display">
                            {balance !== null ? formatCurrency(balance) : 'Loading...'}
                          </div>
                          <span className="balance-status">
                            {balance !== null ? (
                              balance > 0 ? (
                                <><CheckCircle size={14} weight="fill" /> Ready</>
                              ) : (
                                <><Circle size={14} /> Empty</>
                              )
                            ) : '...'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="balance-card-divider"></div>
                      
                      <div className="balance-card-right">
                        <button
                          className={`btn-withdraw ${balance === null ? 'btn-withdraw-disabled' : 'btn-withdraw-active'}`}
                          onClick={() => setShowMoMoWithdrawalModal(true)}
                          disabled={balance === null}
                          title={balance === null ? 'Loading...' : 'Withdraw funds to your MoMo account'}
                        >
                          <ArrowDown size={20} weight="bold" />
                          <span>Withdraw Funds</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="quick-stats-section">
                  <div className="stats-grid">
                    <div className="stat-box">
                      <div className="stat-header">
                        <span className="stat-icon receipt-icon">
                          <Receipt size={22} weight="duotone" />
                        </span>
                        <span className="stat-title">This Week</span>
                      </div>
                      <div className="stat-value">{formatCurrency(recentTransactions.reduce((sum, tx) => sum + (tx.amount || 0), 0))}</div>
                      <div className="stat-meta">{recentTransactions.length} transactions</div>
                    </div>

                    <div className="stat-box">
                      <div className="stat-header">
                        <span className="stat-icon withdrawal-icon">
                          <ArrowDown size={22} weight="duotone" />
                        </span>
                        <span className="stat-title">Total Withdrawn</span>
                      </div>
                      <div className="stat-value">{formatCurrency(withdrawalHistory.reduce((sum, w) => sum + (w.amount || 0), 0))}</div>
                      <div className="stat-meta">{withdrawalHistory.length} withdrawals</div>
                    </div>
                  </div>
                </div>

                {/* Content Grid */}
                <div className="settlements-grid">
                </div>
              </>
            )}
          </div>
        )}

        {/* Fee Details Modal — installment plans + structured discounts */}
        <FeeDetailsModal
          isOpen={showFeeDetailsModal}
          onClose={() => setShowFeeDetailsModal(false)}
          studentId={feeDetailsStudent?.id}
          studentName={feeDetailsStudent?.name}
          onChanged={() => {
            if (view === 'class' && selectedClass) loadClassStatus();
            feesService.getSummary().then(setSummary).catch(() => {});
          }}
        />

        {/* Fee Reminder SMS Modal */}
        <FeeReminderSMSModal
          isOpen={showFeeReminderSMSModal}
          onClose={() => setShowFeeReminderSMSModal(false)}
          student={selectedStudent}
        />

        {/* MoMo Withdrawal Modal */}
        <MoMoWithdrawalModal
          isOpen={showMoMoWithdrawalModal}
          onClose={() => {
            setShowMoMoWithdrawalModal(false);
            loadSettlementData();
          }}
          balance={balance || 0}
          onSuccess={loadSettlementData}
          schoolPhoneNumber={schoolPhoneNumber}
          schoolName={schoolName}
        />

        {/* Dialog Modal */}
        <DialogModal
          isOpen={dialogConfig.isOpen}
          onClose={closeDialog}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type}
          primaryAction={dialogConfig.primaryAction}
          secondaryAction={dialogConfig.secondaryAction}
        />
      </div>
    </Layout>
  );
}

export default FeesPage;
